import { Box, styled } from "@mui/material";
import { keyframes } from "@mui/system";
import { StyledProps } from "../../../interfaces/all";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 8px, 0) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

export const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isFromClient" && prop !== "showTriangle",
})<{ isFromClient: boolean; showTriangle: boolean }>(({ isFromClient }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: isFromClient ? "flex-start" : "flex-end",
  alignItems: "flex-start",
  gap: "6px",
  width: "auto",
  maxWidth: "100%",
  padding: "0 16px",
  animation: `${slideIn} 0.28s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
  willChange: "transform, opacity",
}));

export const MessageGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: "0 16px",
  "& > div + div": {
    marginTop: "1px",
  },
  "& + &": {
    marginTop: "4px",
  },
});

export const MessageWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isFromSameSender" && prop !== "isFirstInGroup",
})<{ isFromSameSender?: boolean; isFirstInGroup?: boolean }>(
  ({ isFromSameSender, isFirstInGroup }) => ({
    display: "flex",
    flexDirection: "column",
    marginTop: isFirstInGroup ? "4px" : isFromSameSender ? "1px" : "4px",
  }),
);

export const SenderGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  "& > div + div": {
    marginTop: "2px",
  },
  "& + &": {
    marginTop: "8px",
  },
});

export const MessageContent = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isFromClient" && prop !== "showTriangle",
})<StyledProps & { showTriangle?: boolean }>(
  ({ isFromClient, showTriangle = true }) => ({
    position: "relative",
    maxWidth: "70%",
    width: "auto",
    minWidth: "60px",
    backgroundColor: isFromClient ? "#ffffff" : "#d9fdd3",
    color: "#111b21",
    borderRadius: isFromClient
      ? showTriangle
        ? "0 7.5px 7.5px 7.5px"
        : "7.5px"
      : showTriangle
        ? "7.5px 0 7.5px 7.5px"
        : "7.5px",
    padding: "8px 8px 4px 8px",
    boxShadow: "0 1px 0.5px rgba(11, 20, 26, 0.13)",
    overflow: "visible",
    wordBreak: "break-word",
    transition:
      "background-color 0.2s cubic-bezier(0.22, 1, 0.36, 1), transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "background-color, transform",
    "&:hover": {
      backgroundColor: isFromClient ? "#f5f6f6" : "#d1f7c4",
      transform: "translate3d(0, -1px, 0)",
    },
    "&::before": {
      content: showTriangle ? '""' : "none",
      position: "absolute",
      top: "0",
      left: isFromClient ? "-8px" : "auto",
      right: isFromClient ? "auto" : "-8px",
      width: "0",
      height: "0",
      borderStyle: "solid",
      borderWidth: "0 9px 9px 0",
      borderColor: isFromClient
        ? "transparent #ffffff transparent transparent"
        : "transparent #d9fdd3 transparent transparent",
      transform: isFromClient ? "none" : "scaleX(-1)",
      transition: "border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
      "&:hover": {
        borderColor: isFromClient
          ? "transparent #f5f6f6 transparent transparent"
          : "transparent #d1f7c4 transparent transparent",
      },
    },
  }),
);

export const MessageText = styled(Box)({
  fontSize: "14.2px",
  lineHeight: "19px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  display: "inline-block",
  paddingBottom: "1px",
  maxWidth: "100%",
  overflow: "hidden",
});

export const MessageTextContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  maxWidth: "100%",
  overflow: "hidden",
});

export const MessageTextLine = styled(Box)({
  display: "block",
  minHeight: "20px",
  position: "relative",
  maxWidth: "100%",
  overflow: "hidden",
});

export const MessageCaption = styled(Box)({
  fontSize: "14.2px",
  lineHeight: "19px",
  color: "#667781",
  marginTop: "4px",
  marginBottom: "4px",
});

export const MessageInfo = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "0 2px",
  minWidth: "52px",
  justifyContent: "flex-end",
  position: "absolute",
  right: "0",
  bottom: "2px",
  height: "18px",
  zIndex: 1,
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
  backgroundColor: "inherit",
});

export const MessageActions = styled(Box)({
  position: "absolute",
  right: "8px",
  bottom: "8px",
  display: "none",
  gap: "4px",
  ".image-actions": {
    display: "flex",
  },
});

export const AvatarContainer = styled(Box)({
  display: "flex",
  alignItems: "flex-end",
  marginRight: "6px",
  marginLeft: "6px",
  minWidth: "36px",
  width: "36px",
  flexShrink: 0,
});

export const MessageSender = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isFromClient",
})<StyledProps>(({ isFromClient }) => ({
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#667781",
  marginBottom: "2px",
  opacity: 0.9,
}));
